<template>
  <div>
    <VorgangTable
      v-if="vorgaenge"
      :displayFieldsByKey="['id', 'kunde', 'npx', 'releaseprocessActions1', 'releaseProzessState']"
      :isLoading="isLoading"
      :items="mergeVorgaengeMitBOFiles"
      :rowSelectBlockExpression="rowSelectBlockExpression"
      :dokumenteCheckRequired="dokumenteCheckRequired"
      :passdatenCheckRequired="passdatenCheckRequired"
      :transferData="transferData"
      :isReiseOhneFlug="isReiseOhneFlug"
      releaseprocessActions1Label="Reiseleistungen & Listenversand"
    >
      <template v-slot:releaseprocessActions1Head="data">
        <div class="text-nowrap">
          <AppSelect
            v-model="allRowsListenversand"
            :options="bulkListenversandOptions"
            :disabled="reiseterminObj?.state !== 'Aufgelegt' || !isEveryVorgangBuchungAndNotReleased"
            label="Alle Zeilen anpassen"
            placeholder="Alle Zeilen anpassen"
            hide-search
            size="sm"
            class="mb-1 zellen-select"
            @change="onChangeMultipleListenversand"
          />
        </div>
        <div class="text-nowrap">Reiseleistungen & Listenversand</div>
      </template>
      <template v-slot:releaseprocessActions1="data">
        <AppSelect
          v-model="data.vorgang.gebuchteReiseleistungListenversandGeprueft"
          :options="listenversandOptions"
          :disabled="
            reiseterminObj?.state !== 'Aufgelegt' ||
            !isReiseterminInReleasePruefung ||
            !(data.isVorgangBuchung || data.isStornierteBuchung)
          "
          label="Reiseleistungen"
          hide-search
          size="sm"
          class="zellen-select"
          @change="onChangeListenversand(data)"
        />
      </template>
    </VorgangTable>
  </div>
</template>

<script>
import VorgangTable from '@/components/vorgaenge/vorgang-table.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { UPDATE_VORGANG_RELEASEPROZESS } from '../../../core/vorgaenge/stores/vorgaenge.module';
import * as flightHelpers from '../../../core/common/helpers/reisetermine-card-vorgaenge-fluege-helper';
import { isReiseOhneFlug } from '../../../core/common/helpers/utils';

export default {
  components: { VorgangTable, AppSelect },
  props: {
    isLoading: { type: Boolean },
    passdatenCheckRequired: { type: Boolean },
    isReiseterminInReleasePruefung: { type: Boolean },
    dokumenteCheckRequired: { type: Boolean },
    vorgaenge: { type: Array },
    reiseterminObj: { type: Object },
    transferData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      allRowsListenversand: null,
      flugbuchungVorgaenge: [],
      isLoadingFlugbuchungen: false,
      flugstreckenLoaded: false,
    };
  },
  computed: {
    // Prüfstatus der Reiseleistungen, im Kopf der Spalte in abweichender Reihenfolge
    listenversandOptions() {
      return [
        { value: 'NichtGeprueft', text: this.$t('REISETERMINE.RELEASEPROZESS.NICHTGEPRUEFT') },
        {
          value: 'GeprueftNichtKorrektInArbeit',
          text: this.$t('REISETERMINE.RELEASEPROZESS.GEPRUEFTINARBEIT'),
        },
        { value: 'GeprueftVorhandenKorrekt', text: this.$t('REISETERMINE.RELEASEPROZESS.GEPRUEFTKORREKT') },
      ];
    },
    bulkListenversandOptions() {
      return [
        { value: 'NichtGeprueft', text: this.$t('REISETERMINE.RELEASEPROZESS.NICHTGEPRUEFT') },
        { value: 'GeprueftVorhandenKorrekt', text: this.$t('REISETERMINE.RELEASEPROZESS.GEPRUEFTKORREKT') },
        {
          value: 'GeprueftNichtKorrektInArbeit',
          text: this.$t('REISETERMINE.RELEASEPROZESS.GEPRUEFTINARBEIT'),
        },
      ];
    },
    isReiseOhneFlug() {
      return isReiseOhneFlug(this.reiseterminObj?.metadata);
    },
    isEveryVorgangBuchungAndNotReleased() {
      return (
        this.isReiseterminInReleasePruefung &&
        this.vorgaenge.every(
          vorgang => vorgang?.blankState === 'Buchung' || vorgang?.blankState === 'StornierteBuchung'
        )
      );
    },
    mergeVorgaengeMitBOFiles() {
      if (!this.vorgaenge) return [];
      if (!this.flugbuchungVorgaenge || this.flugbuchungVorgaenge.length === 0) {
        return this.vorgaenge;
      }

      return this.vorgaenge.map(vorgang => {
        const flugbuchungData = this.flugbuchungVorgaenge.find(fv => fv.id === vorgang.id);
        if (flugbuchungData) {
          return {
            ...vorgang,
            statusPruefung: flugbuchungData.statusPruefung,
            statusPruefungAkzeptiert: flugbuchungData.statusPruefungAkzeptiert,
            flugbuchungen: flugbuchungData.flugbuchungen,
            teilnehmers: flugbuchungData.teilnehmers,
          };
        }
        return vorgang;
      });
    },
  },
  mounted() {
    this.loadFlugbuchungenAusBOFile();
  },
  watch: {
    vorgaenge: {
      handler() {
        this.flugstreckenLoaded = false;
        this.loadFlugbuchungenAusBOFile();
      },
      deep: true,
    },
  },
  methods: {
    rowSelectBlockExpression(item) {
      return (
        this.isReiseterminInReleasePruefung &&
        (item === 'Buchung' || item === 'StornierteBuchung') &&
        !item.isReleased
      );
    },
    onChangeListenversand(data) {
      const { vorgang } = data;
      const { id, gebuchteReiseleistungListenversandGeprueft } = vorgang;
      const params = [
        {
          vorgangId: id,
          gebuchteReiseleistungListenversandGeprueft,
        },
      ];
      this.$store.dispatch(UPDATE_VORGANG_RELEASEPROZESS, params).then(() => {
        this.toast('Gespeichert.');
        this.$emit('workflow-updated');
      });
    },
    onChangeMultipleListenversand() {
      const params = [];
      for (const vorgang of this.vorgaenge) {
        params.push({
          vorgangId: vorgang.id,
          gebuchteReiseleistungListenversandGeprueft: this.allRowsListenversand,
        });
      }
      this.$store.dispatch(UPDATE_VORGANG_RELEASEPROZESS, params).then(() => {
        this.toast('Alle Zeilen wurden gespeichert.');
        this.$emit('workflow-updated');
        this.vorgaenge.map(
          vorgang => (vorgang.gebuchteReiseleistungListenversandGeprueft = this.allRowsListenversand)
        );
      });
    },
    async loadFlugbuchungenAusBOFile() {
      if (!this.vorgaenge || this.vorgaenge.length === 0) return;
      if (this.flugstreckenLoaded) return;

      this.isLoadingFlugbuchungen = true;
      try {
        this.flugbuchungVorgaenge = await flightHelpers.loadFlugbuchungenAusBOFile(this.vorgaenge);
        this.flugstreckenLoaded = true;
      } catch (error) {
        console.error('Error loading detailed flight data:', error);
      } finally {
        this.isLoadingFlugbuchungen = false;
      }
    },
  },
};
</script>

<style scoped>
.zellen-select {
  --app-select-menu-max-width: 280px;
}
</style>
